import React, { useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { API_BASE_URL } from '../config/api';
import { X, Plus, Trash2, Upload, Download, FileText, Send, Receipt } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const CONTRACT_BADGES = {
  draft: 'bg-gray-100 text-gray-700', signed: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800', completed: 'bg-purple-100 text-purple-800'
};
const MILESTONE_BADGES = {
  pending: 'bg-gray-100 text-gray-600', in_progress: 'bg-yellow-100 text-yellow-800', completed: 'bg-green-100 text-green-800'
};

export const EngagementManager = ({ engagement, onClose, onChanged }) => {
  const c = engagement.contract || null;
  const [contractForm, setContractForm] = useState({
    scope: c?.scope || '',
    fee_amount: c?.fee_amount ?? '',
    start_date: c?.start_date || '',
    end_date: c?.end_date || '',
    milestones: c?.milestones?.map(m => ({ title: m.title, due_date: m.due_date || '' })) || []
  });
  const [busy, setBusy] = useState(false);
  const [progressText, setProgressText] = useState('');
  const fileRef = React.useRef(null);

  const api = (path) => `${API_BASE_URL}/api/experts/admin/engagements/${engagement.id}${path}`;
  const opts = { headers: authHeaders(), withCredentials: true };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged(); }
    catch (e) { alert(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const saveContract = () => run(() => axios.post(api('/contract'), {
    ...contractForm,
    fee_amount: contractForm.fee_amount === '' ? null : Number(contractForm.fee_amount),
    milestones: contractForm.milestones.filter(m => m.title.trim())
  }, opts));

  const setContractStatus = (status) => run(() => axios.post(api('/contract/status'), { status }, opts));
  const setMilestoneStatus = (milestone_id, status) => run(() => axios.post(api('/milestone-status'), { milestone_id, status }, opts));
  const addProgress = () => run(async () => {
    await axios.post(api('/progress'), { text: progressText }, opts);
    setProgressText('');
  });
  const generateInvoice = () => run(() => axios.post(api('/invoice'), {}, opts));
  const sendInvoice = () => run(async () => {
    const { data } = await axios.post(api('/invoice/send'), {}, opts);
    alert(data.message);
  });
  const createPaymentLink = () => run(() => axios.post(api('/invoice/payment-link'), {}, opts));
  const syncPayment = () => run(async () => {
    const { data } = await axios.post(api('/invoice/sync'), {}, opts);
    alert(data.status === 'paid' ? 'Payment received! Ledger marked as Received.' : `Payment link status: ${data.status}`);
  });

  const uploadSigned = (file) => {
    if (!file) return;
    run(async () => {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(api('/contract/upload'), fd, opts);
    });
  };

  const downloadFile = async (url, name) => {
    try {
      const res = await axios.get(url, { ...opts, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.download = name;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);
    } catch (e) { alert('Download failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="engagement-manager-modal">
      <Card className="w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-xl font-bold text-[#1E3A5F]">Manage Assignment</h3>
            <p className="text-sm text-gray-500">{engagement.expert_name} × {engagement.organization_name || engagement.member_name || engagement.member_email}</p>
          </div>
          <button onClick={onClose} data-testid="manager-close-btn"><X className="h-5 w-5 text-gray-400" /></button>
        </div>

        {/* ===== CONTRACT ===== */}
        <div className="mb-6 border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h4 className="font-bold text-[#1E3A5F] flex items-center gap-2"><FileText className="h-5 w-5 text-[#E87722]" />Contract</h4>
            {c && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{c.contract_number}</span>
                <select value={c.status} onChange={(e) => setContractStatus(e.target.value)} disabled={busy}
                  className={`text-xs rounded-md px-2 py-1 border ${CONTRACT_BADGES[c.status]}`} data-testid="contract-status-select">
                  <option value="draft">Draft</option>
                  <option value="signed">Signed</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scope of Work *</label>
              <Textarea rows={3} value={contractForm.scope}
                onChange={(e) => setContractForm({ ...contractForm, scope: e.target.value })}
                placeholder="Deliverables, geography, methodology..." data-testid="contract-scope-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fee (₹)</label>
                <Input type="number" min="0" value={contractForm.fee_amount}
                  onChange={(e) => setContractForm({ ...contractForm, fee_amount: e.target.value })} data-testid="contract-fee-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <Input type="date" value={contractForm.start_date}
                  onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })} data-testid="contract-start-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <Input type="date" value={contractForm.end_date}
                  onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })} data-testid="contract-end-input" />
              </div>
            </div>

            {/* Milestones editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Milestones</label>
              {contractForm.milestones.map((m, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input placeholder={`Milestone ${idx + 1}`} value={m.title}
                    onChange={(e) => {
                      const ms = [...contractForm.milestones]; ms[idx].title = e.target.value;
                      setContractForm({ ...contractForm, milestones: ms });
                    }} data-testid={`milestone-title-input-${idx}`} />
                  <Input type="date" className="w-40" value={m.due_date}
                    onChange={(e) => {
                      const ms = [...contractForm.milestones]; ms[idx].due_date = e.target.value;
                      setContractForm({ ...contractForm, milestones: ms });
                    }} />
                  <Button size="sm" variant="ghost" className="text-red-500"
                    onClick={() => setContractForm({ ...contractForm, milestones: contractForm.milestones.filter((_, i) => i !== idx) })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline"
                onClick={() => setContractForm({ ...contractForm, milestones: [...contractForm.milestones, { title: '', due_date: '' }] })}
                data-testid="add-milestone-btn">
                <Plus className="h-4 w-4 mr-1" />Add Milestone
              </Button>
            </div>

            <div className="flex gap-2 flex-wrap items-center pt-2">
              <Button onClick={saveContract} disabled={busy || !contractForm.scope.trim()}
                className="bg-[#1E3A5F] hover:bg-[#2d5a8f]" data-testid="save-contract-btn">
                {busy ? 'Working...' : c ? 'Update Contract' : 'Create Contract'}
              </Button>
              {c && (
                <>
                  <input type="file" accept=".pdf" className="hidden" ref={fileRef}
                    onChange={(e) => uploadSigned(e.target.files[0])} data-testid="signed-contract-input" />
                  <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="upload-signed-btn">
                    <Upload className="h-4 w-4 mr-1" />{c.signed_doc ? 'Replace Signed PDF' : 'Upload Signed PDF'}
                  </Button>
                  {c.signed_doc && (
                    <button className="text-sm text-[#1E3A5F] underline flex items-center gap-1"
                      onClick={() => downloadFile(`${API_BASE_URL}/api/experts/engagements/${engagement.id}/contract/download`, c.signed_doc.original_name)}
                      data-testid="download-signed-btn">
                      <Download className="h-3 w-3" />{c.signed_doc.original_name}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ===== WORKFLOW: MILESTONE STATUS + PROGRESS ===== */}
        {c && c.milestones?.length > 0 && (
          <div className="mb-6 border rounded-xl p-5" data-testid="workflow-section">
            <h4 className="font-bold text-[#1E3A5F] mb-3">Milestone Progress</h4>
            <div className="space-y-2">
              {c.milestones.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg p-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{m.title}</p>
                    {m.due_date && <p className="text-xs text-gray-400">Due: {m.due_date}</p>}
                  </div>
                  <select value={m.status} onChange={(e) => setMilestoneStatus(m.id, e.target.value)} disabled={busy}
                    className={`text-xs rounded-md px-2 py-1 border ${MILESTONE_BADGES[m.status]}`}
                    data-testid={`milestone-status-select-${m.id}`}>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6 border rounded-xl p-5">
          <h4 className="font-bold text-[#1E3A5F] mb-3">Progress Updates <span className="text-xs text-gray-400 font-normal">(visible to the member)</span></h4>
          <div className="flex gap-2 mb-3">
            <Input placeholder="e.g. Baseline data collection completed in District 1..."
              value={progressText} onChange={(e) => setProgressText(e.target.value)} data-testid="progress-text-input" />
            <Button onClick={addProgress} disabled={busy || !progressText.trim()}
              className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid="add-progress-btn">Post</Button>
          </div>
          {(engagement.progress_updates || []).slice().reverse().map((u) => (
            <div key={u.id} className="border-l-2 border-[#E87722]/40 pl-3 mb-2" data-testid={`progress-update-${u.id}`}>
              <p className="text-sm text-gray-700">{u.text}</p>
              <p className="text-xs text-gray-400">{new Date(u.at).toLocaleString('en-IN')}</p>
            </div>
          ))}
        </div>

        {/* ===== INVOICE ===== */}
        <div className="border rounded-xl p-5">
          <h4 className="font-bold text-[#1E3A5F] mb-3 flex items-center gap-2"><Receipt className="h-5 w-5 text-[#E87722]" />Commission Invoice (5%)</h4>
          {engagement.invoice ? (
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-green-100 text-green-800">{engagement.invoice.invoice_number}</Badge>
                <span className="text-sm font-bold text-[#E87722]">{inr(engagement.invoice.amount)}</span>
                {engagement.invoice.status === 'paid' && (
                  <Badge className="bg-green-600 text-white" data-testid="invoice-paid-badge">PAID ✓</Badge>
                )}
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => downloadFile(`${API_BASE_URL}/api/experts/admin/engagements/${engagement.id}/invoice/pdf`, `${engagement.invoice.invoice_number.replace(/\//g, '-')}.pdf`)}
                  data-testid="download-invoice-btn">
                  <Download className="h-4 w-4 mr-1" />PDF
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={sendInvoice}
                  className="border-[#E87722] text-[#E87722] hover:bg-[#E87722] hover:text-white" data-testid="send-invoice-btn">
                  <Send className="h-4 w-4 mr-1" />Email to Expert
                </Button>
              </div>

              {/* Payment Link */}
              <div className="mt-3 pt-3 border-t">
                {engagement.invoice.payment_link ? (
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    <span className="text-gray-600">Payment link:</span>
                    <a href={engagement.invoice.payment_link.short_url} target="_blank" rel="noreferrer"
                      className="text-[#1E3A5F] underline font-medium" data-testid="payment-link-url">
                      {engagement.invoice.payment_link.short_url}
                    </a>
                    <button className="text-xs text-[#E87722] underline"
                      onClick={() => { navigator.clipboard.writeText(engagement.invoice.payment_link.short_url); alert('Link copied'); }}
                      data-testid="copy-payment-link-btn">
                      Copy
                    </button>
                    <Badge className={engagement.invoice.payment_link.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {engagement.invoice.payment_link.status}
                    </Badge>
                    {engagement.invoice.status !== 'paid' && (
                      <Button size="sm" variant="ghost" disabled={busy} onClick={syncPayment}
                        className="text-[#1E3A5F]" data-testid="sync-payment-btn">
                        Refresh Status
                      </Button>
                    )}
                  </div>
                ) : engagement.invoice.status !== 'paid' && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={createPaymentLink}
                    className="border-green-600 text-green-700 hover:bg-green-600 hover:text-white" data-testid="create-payment-link-btn">
                    Create Razorpay Payment Link
                  </Button>
                )}
                <p className="text-xs text-gray-400 mt-2">When the expert pays via the link, the ledger is marked "Received" automatically. Use "Refresh Status" to reconcile manually.</p>
              </div>
            </div>
          ) : engagement.assignment_fee !== undefined ? (
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-gray-600">Fee recorded: <strong>{inr(engagement.assignment_fee)}</strong> → 5% = <strong className="text-[#E87722]">{inr(engagement.csed_fee)}</strong></p>
              <Button size="sm" onClick={generateInvoice} disabled={busy}
                className="bg-[#1E3A5F] hover:bg-[#2d5a8f]" data-testid="generate-invoice-btn">
                Generate Invoice
              </Button>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Record the assignment fee (Engagement Requests tab) to enable invoicing.</p>
          )}
        </div>
      </Card>
    </div>
  );
};
